import { type ReactNode } from 'react';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

type MetricCardProps = Readonly<{
  id: string;
  label: string;
  /** Numbers are grouped for the locale; strings render as given. */
  value: string | number;
  /** Sits opposite the label, e.g. an info icon. */
  action?: ReactNode;
  /** Sits opposite the value. */
  footer?: ReactNode;
  onClick?: () => void;
}>;

export const MetricCard = ({ id, label, value, action, footer, onClick }: MetricCardProps) => (
  <Box
    id={id}
    onClick={onClick}
    sx={onClick ? ([containerSx, clickableSx] as SxProps<Theme>) : containerSx}
  >
    <Box sx={headerSx}>
      <Typography variant="text-sm-medium" sx={labelSx}>
        {label}
      </Typography>
      {action}
    </Box>
    <Box sx={valueRowSx}>
      <Typography variant="display-sm-semibold" sx={valueSx}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      {footer ? <Box sx={footerSx}>{footer}</Box> : null}
    </Box>
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  minWidth: 0,
  padding: backrs.spacing['spacing-2xl'],
  boxSizing: 'border-box',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const clickableSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translate(-2px, -2px)',
  },
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const valueRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
});

const footerSx: SxProps<Theme> = {
  flexShrink: 0,
};

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const valueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  whiteSpace: 'nowrap',
});
